import { useEffect, useState } from 'react';

import { getDownloadURL, ref } from 'firebase/storage';

import { ISanitizedCompany } from 'types/company';

import { getCompany } from 'services/api/firestore/client';

import { storage } from 'clients/firebase';

// The `company` prop comes from a server layout that can be cached (ISR routes
// such as `/library` revalidate daily), so after a save it may still hold the old
// settings. While `enabled`, this reads the company from Firestore instead, for the
// settings drawer to seed its form from. `null` while loading; on a failed read it
// falls back to the prop, and the drawer's changed-fields write limits the damage.
export const useFreshCompany = (
  company: ISanitizedCompany,
  enabled: boolean,
) => {
  const [freshCompany, setFreshCompany] = useState<ISanitizedCompany | null>(
    null,
  );

  useEffect(() => {
    if (!enabled) {
      setFreshCompany(null);
      return;
    }

    let cancelled = false;

    // Uploaded logos live under the company's folder, which members can read.
    // A default logo (empty path, or the shared folder) keeps the prop's URL.
    const logoUrl = async (
      path: string | null,
      fallback: string | null,
    ): Promise<string | null> => {
      if (!path || !path.startsWith(`${company.id}/`)) {
        return fallback;
      }

      try {
        return await getDownloadURL(ref(storage, path));
      } catch (error) {
        console.error('Could not resolve company logo', error);
        return fallback;
      }
    };

    (async () => {
      try {
        const doc = await getCompany(company.id);

        if (!doc) {
          throw new Error(`Company ${company.id} could not be read`);
        }

        const {
          logoPath,
          logoDarkPath,
          logoQRPath,
          integrations: _secrets,
          ...settings
        } = doc;
        const [light, dark, qr] = await Promise.all([
          logoUrl(logoPath, company.logoUrl),
          logoUrl(logoDarkPath, company.logoDarkUrl),
          logoUrl(logoQRPath, company.logoQRUrl),
        ]);

        if (!cancelled) {
          setFreshCompany({
            ...company,
            ...settings,
            logoUrl: light ?? company.logoUrl,
            logoDarkUrl: dark ?? company.logoDarkUrl,
            logoQRUrl: qr,
          });
        }
      } catch (error) {
        console.error('Could not read company settings', error);

        if (!cancelled) {
          setFreshCompany(company);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // Re-read per opening, not per new `company` reference from a refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, company.id]);

  return { freshCompany };
};
